import { useMemo } from 'react';

import { useWorkloadsOptimizationSummaryQuery } from '@cast/features-cluster-shared';
import { useCluster } from '@cast/shared-cluster-context-data-access';

export type WorkloadAutoscalerCoverageData = {
  percentage: number;
  cpuCoresDifference: number;
  totalCount: number;
  verticalCount: number;
  horizontalCount: number;
  bothCount: number;
  disabledCount: number;
};

export const useWorkloadAutoscalerCoverageData = (): {
  data: WorkloadAutoscalerCoverageData | undefined;
  isLoading: boolean;
  isFetching: boolean;
  hasError: boolean;
  refetch: () => void;
} => {
  const { cluster } = useCluster();
  const { data, isLoading, isFetching, error, refetch } =
    useWorkloadsOptimizationSummaryQuery({
      clusterId: cluster.id,
      enabled: !!cluster.id,
    });

  const normalizedData = useMemo(() => {
    if (!data) {
      return undefined;
    }

    const {
      totalCount,
      optimizedCount,
      vpaOptimizedCount,
      hpaOptimizedCount,
      hpaVpaOptimizedCount,
      cpuCoresDifference,
    } = data;

    return {
      percentage: totalCount > 0 ? (optimizedCount / totalCount) * 100 : 0,
      cpuCoresDifference,
      totalCount,
      verticalCount: vpaOptimizedCount - hpaVpaOptimizedCount,
      horizontalCount: hpaOptimizedCount - hpaVpaOptimizedCount,
      bothCount: hpaVpaOptimizedCount,
      disabledCount: totalCount - optimizedCount,
    };
  }, [data]);

  return {
    data: normalizedData,
    isLoading,
    isFetching,
    hasError: !!error,
    refetch,
  };
};
